// Utility functions for name formatting and display
// CREATED: Utilities for formatting user names in different contexts
// UPDATED: Added capitalizeFirstName function to properly format names with compound support (Jean-Pierre, Marie Claire)

export interface NameProfile {
  first_name: string;
  last_name: string;
}

/**
 * Format a name to short display format: FirstName L.
 * Example: "Thomas Durant" -> "Thomas D."
 */
export function getShortName(profile: NameProfile): string {
  const firstName = profile.first_name;
  const lastInitial = profile.last_name.charAt(0).toUpperCase();
  return `${firstName} ${lastInitial}.`;
}

/**
 * Get initials from a name profile
 * Example: "Thomas Durant" -> "TD"
 */
export function getInitials(profile: NameProfile): string {
  const firstInitial = profile.first_name.charAt(0).toUpperCase();
  const lastInitial = profile.last_name.charAt(0).toUpperCase();
  return `${firstInitial}${lastInitial}`;
}

/**
 * Truncate a name to a maximum length with ellipsis
 */
export function truncateName(name: string, maxLength: number = 20): string {
  if (name.length <= maxLength) {
    return name;
  }
  return name.substring(0, maxLength - 3) + '...';
}

/**
 * Capitalize first name properly, handling compound names
 * Example: "jean-pierre" -> "Jean-Pierre"
 * Example: "marie claire" -> "Marie Claire"
 */
export function capitalizeFirstName(name: string): string {
  if (!name) return '';

  // Handle hyphenated names (e.g., Jean-Pierre)
  if (name.includes('-')) {
    return name
      .split('-')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join('-');
  }

  // Handle space-separated names (e.g., Marie Claire)
  if (name.includes(' ')) {
    return name
      .split(' ')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  // Simple name
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}
